/**
 * The OptiReach winback claim, for the lapsed paywall. Same contract as the old paywall
 * (`components/layout/InactiveAccountDialog.tsx`) and LoginPage's `claimWinbackTrial`:
 * a stored capture offers "Claim your 30-day free trial"; a granted claim refetches
 * ME_QUERY + GET_COMPANY_INFO into the auth store, which flips `isAccountInactive` and
 * lifts the paywall.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  REACTIVATE_WITH_TRIAL,
  ME_QUERY,
  GET_COMPANY_INFO,
  type MeQueryResult,
  type GetCompanyInfoQueryResult,
  type ReactivateWithTrialResult,
  type ReactivateWithTrialVariables,
} from '@scio3/graphql-client';
import { analyticsReactivated } from '@/lib/analytics-service';
import { getStoredWinbackUtm, clearWinbackUtm } from '@/lib/winback-utm';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Reason-specific copy for a rejected claim. The server returns the cause as `WINBACK_*` in
 * `message` (an in-band rejection, not a thrown GraphQL error), so a terminal rejection
 * (already claimed / cap reached) doesn't read as a transient glitch.
 */
function winbackClaimError(code: string, t: (key: string) => string): string {
  switch (code) {
    case 'WINBACK_ALREADY_CLAIMED':
      return t('You have already claimed this free trial offer.');
    case 'WINBACK_LIMIT_REACHED':
    case 'WINBACK_NOT_ELIGIBLE':
    case 'WINBACK_FORCE_INACTIVE':
      return t('This free trial offer is not available for your account.');
    default:
      return t('Failed to claim your free trial. Please try again or contact support.');
  }
}

export function useWinbackClaim() {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const setMe = useAuthStore((s) => s.setMe);
  const setCompany = useAuthStore((s) => s.setCompany);
  const [reactivateWithTrial] = useMutation<
    ReactivateWithTrialResult,
    ReactivateWithTrialVariables
  >(REACTIVATE_WITH_TRIAL);
  const [isClaiming, setIsClaiming] = useState(false);

  // Read on every render, like the old paywall: a spent capture hides the button next render.
  const winbackUtm = getStoredWinbackUtm();

  const claim = async () => {
    if (!winbackUtm) return;
    setIsClaiming(true);
    try {
      const { data } = await reactivateWithTrial({ variables: { utmData: winbackUtm } });
      // A definitive server answer, granted OR rejected, spends the capture. Transport
      // failures throw before this, so the capture survives for a retry.
      clearWinbackUtm();
      if (!data?.reactivateWithTrial?.status) {
        throw new Error(data?.reactivateWithTrial?.message ?? 'reactivateWithTrial failed');
      }
      analyticsReactivated({ campaign: winbackUtm.utmCampaign ?? '' });
      const [meResult, companyResult] = await Promise.all([
        apolloClient.query<MeQueryResult>({ query: ME_QUERY, fetchPolicy: 'network-only' }),
        apolloClient.query<GetCompanyInfoQueryResult>({
          query: GET_COMPANY_INFO,
          fetchPolicy: 'network-only',
        }),
      ]);
      if (meResult.data?.me) setMe(meResult.data.me);
      if (companyResult.data?.getCompanyInfo) setCompany(companyResult.data.getCompanyInfo);
    } catch (err: unknown) {
      const code = err instanceof Error ? err.message : '';
      console.warn('[winback] paywall claim failed:', code || err);
      toast.error(winbackClaimError(code, t));
    } finally {
      setIsClaiming(false);
    }
  };

  return { hasWinbackOffer: !!winbackUtm, isClaiming, claim };
}
